import React, { useEffect, useRef, useState } from 'react';
import 'twin.macro';
import { v4 as uuid } from 'uuid';
import { ArrowPathIcon, PlusIcon } from '@heroicons/react/24/outline';
import emptyBo from 'components/Bo/emptyBo';
import Button from 'components/Buttons/Button';
import { useTranslation } from 'react-i18next';
import { CJAtelier, GroupPivot } from 'types/cj';
import { editorCJSeancePayloadAtom } from 'containers/CahierJournal/UpdateCJSeanceModal/atom';
import { useAtom } from 'jotai';
import { Eleve } from 'types/eleves';
import { EleveGroup } from 'types/eleveGroups';
import { useCreateEleveGroup } from 'queries/eleveGroups/useCreateEleveGroup';
import { useDeleteEleveGroup } from 'queries/eleveGroups/useDeleteEleveGroup';
import { useUpdateEleveGroup } from 'queries/eleveGroups/useUpdateEleveGroup';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SwitchToggle } from 'components/Form/SwitchToggle';
import { RotateInSeanceTable } from 'containers/CahierJournal/EventModal/ModalBody/Ateliers/RotateInSeanceTable';
import { useField } from 'react-final-form';
import { GroupSelectorInline } from './GroupSelectorInline'; // see snippet below
import { SortableAtelier } from './SortableAtelier';
import { isRotateInSeance } from './isRotateInSeance';
import { useGroups } from 'containers/CahierJournal/EventModal/ModalBody/Ateliers/useGroups';
import { getGroupsForAtelier } from 'containers/CahierJournal/EventModal/ModalBody/Ateliers/getGroupsForAtelier';
import { getAllPendingEphemeralGroups } from './getAllPendingEphemeralGroups';
import { updatePendingEphemeralGroups } from './updatePendingEphemeralGroups';

/**
 * Round-robin assignment:
 *   - #slices = #ateliers
 *   - group i for slice s => atelier (i + s + offset) mod A
 *   - so each group is in exactly 1 atelier for each slice
 *   - if #groups > #ateliers => multiple groups share the same atelier
 */
function assignRoundRobin(
  ateliers: CJAtelier[],
  groups: EleveGroup[],
  offset: number,
): CJAtelier[] {
  const A = ateliers.length;
  const G = groups.length;
  const newAteliers = ateliers.map(a => ({
    ...a,
    groupPivots: [] as GroupPivot[],
  }));

  // For each group i, across each slice s => 0..A-1
  for (let i = 0; i < G; i++) {
    for (let s = 0; s < A; s++) {
      const aIndex = (i + s + offset) % A;
      newAteliers[aIndex].groupPivots.push({
        id: groups[i].id,
        position: s + 1, // slices = 1..A
      });
    }
  }
  return newAteliers;
}

// Minimal newEmptyAtelier
export function newEmptyAtelier({
  title,
  cycle,
  supervision,
  groupId,
  position,
}: {
  title: string;
  cycle: string;
  supervision: string;
  groupId?: string;
  position?: number;
}): CJAtelier {
  return {
    id: uuid(),
    title,
    cycle,
    flow: '',
    bo: emptyBo,
    materials: [],
    linkedFichesDePrep: [],
    attachments: [],
    supervision,
    position,
    groupPivots: groupId
      ? [
          {
            id: groupId,
            position: 1,
          },
        ]
      : [],
  };
}

// SortableAtelier omitted for brevity. We'll focus on AteliersForm below.

interface Props {
  eleves: Eleve[];
  ateliers: CJAtelier[];
  onChange: (ateliers: CJAtelier[]) => void;
  cycle?: string;
}

function getGroupIdsInAteliers(ateliers: CJAtelier[]): string[] {
  return ateliers
    .flatMap(atelier => atelier.groupPivots || [])
    .map(groupPivot => groupPivot.id);
}

/**
 * AteliersForm - Manages workshop groups with complex ephemeral group handling
 *
 * ARCHITECTURAL OVERVIEW - GROUP TYPES:
 *
 * 1. REGULAR GROUPS (EleveGroup with isEphemeral=false):
 *    - Named, persistent groups created and saved in database
 *    - Have a color and name (e.g., "Groupe Rouge", "CM1")
 *    - Reusable across multiple seances
 *    - Created/updated/deleted via useCreateEleveGroup/useUpdateEleveGroup/useDeleteEleveGroup
 *
 * 2. EPHEMERAL GROUPS (EleveGroup with isEphemeral=true):
 *    - Temporary individual student selections for a specific seance
 *    - Display as student avatars instead of group name
 *    - Two states:
 *      a) SAVED EPHEMERAL: Already persisted in database (have real IDs)
 *      b) PENDING EPHEMERAL: Created locally, not yet saved (temporary UUIDs)
 *
 * DATA FLOW & STATE MANAGEMENT:
 *
 * - useGroups hook fetches all groups and:
 *   1. Separates regular groups from saved ephemeral groups
 *   2. Converts pendingEphemeralGroups (id + elevesIds) to full EleveGroup objects
 *   3. Deduplicates: if same ID exists in both saved and pending, uses saved version
 *   4. Returns: eleveGroups (all), regularGroups, ephemeralGroups (all including pending), savedEphemeralGroups
 *
 * - pendingEphemeralGroups stored in CJAtelier objects:
 *   - Temporary storage for ephemeral groups before save
 *   - Structure: { id: string, elevesIds: string[] }
 *   - Cleaned (removed) after successful save via serializer
 *   - Allows optimistic UI updates before API calls
 *
 * ROTATION MODE BEHAVIOR:
 *
 * - When enabled, groups rotate between ateliers over time slices
 * - Round-robin assignment: each group visits each atelier once
 * - New ephemeral groups must be assigned to at least one atelier to appear
 * - handlePendingEphemeralGroupsChange ensures new groups go to first atelier
 *
 * KEY DESIGN DECISIONS:
 *
 * 1. Why pendingEphemeralGroups?
 *    - Enables creating ephemeral groups before saving the seance
 *    - Provides optimistic UI without immediate API calls
 *    - Prevents creating unnecessary database entries if user cancels/closes modal
 *    - Groups only persist to database when user explicitly saves the seance
 *
 * 2. Deduplication strategy:
 *    - Prevents same group appearing twice (as saved and pending)
 *    - Saved version takes precedence (source of truth)
 *    - Pending groups filtered out if matching saved group exists
 *
 * 3. Auto-assignment in rotation:
 *    - New ephemeral groups added to first atelier automatically
 *    - Ensures they participate in rotation immediately
 *    - Without this, groups would be created but not visible
 *
 * 4. Cleanup after save:
 *    - pendingEphemeralGroups cleared in serializer and optimistic updates
 *    - Prevents stale pending groups from persisting
 *    - Saved ephemeral groups become the source of truth
 */
export function AteliersForm(props: Props) {
  const { ateliers, onChange, cycle = 'Cycle 2' } = props;
  const { t } = useTranslation(['cahierJournal']);
  const startField = useField('start');
  const endField = useField('end');
  const start = startField.input.value;
  const end = endField.input.value;

  // from external store, e.g. a CJ editor payload
  const [editorPayload] = useAtom(editorCJSeancePayloadAtom);
  const openedAtelierId = editorPayload?.atelierId;

  const { eleveGroups, regularGroups, ephemeralGroups, savedEphemeralGroups } =
    useGroups({
      start,
      end,
      ateliers,
      eleves: props.eleves,
    });

  const allPendingEphemeralGroups = getAllPendingEphemeralGroups(ateliers);

  const handlePendingEphemeralGroupsChange = (
    updatedGroups: Array<{ id: string; elevesIds: string[] }>,
  ) => {
    const updatedAteliers = updatePendingEphemeralGroups(
      ateliers,
      updatedGroups,
    );
    onChange(updatedAteliers);
  };

  // Some local states
  const [createdAtelierId, setCreatedAtelierId] = useState<string | null>(null);

  // We'll keep an offset for the "Rotate Groups" button
  const [roundOffset, setRoundOffset] = useState(0);

  // "selectedGroupIds" for GroupSelector
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(
    getGroupIdsInAteliers(ateliers),
  );

  // Refs for scrolling to a just-opened atelier
  const atelierRefs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    atelierRefs.current = atelierRefs.current.slice(0, ateliers.length);
  }, [ateliers.length]);

  useEffect(() => {
    const atelierIndex = ateliers.findIndex(a => a.id === openedAtelierId);
    if (atelierIndex !== -1) {
      const node = atelierRefs.current[atelierIndex];
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [openedAtelierId]); // do not add `ateliers` as dependency : it would scroll on every change

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const { mutateAsync: onCreateGroup } = useCreateEleveGroup();
  const { mutateAsync: onDeleteGroup } = useDeleteEleveGroup();
  const { mutateAsync: onUpdateGroup } = useUpdateEleveGroup();

  // 1) Our computed "rotateInSeance" from data
  const rotateInSeance = isRotateInSeance(ateliers);

  // ---------- DnD reorder logic
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = ateliers.findIndex(a => a.id === active.id);
    const newIndex = ateliers.findIndex(a => a.id === over.id);

    const newAteliers = arrayMove(ateliers, oldIndex, newIndex).map(
      (atelier, idx) => ({ ...atelier, position: idx }),
    );
    onChange(newAteliers);
  };

  // ---------- KEY PART: Auto-run round-robin if rotateInSeance is ON
  // every time we have changes in (selectedGroupIds, roundOffset, ateliers, etc.)
  useEffect(() => {
    if (!rotateInSeance) {
      return;
    }
    if (!eleveGroups) return;
    if (ateliers.length === 0) return;

    // Filter only the selected groups
    const included = eleveGroups.filter(g => selectedGroupIds.includes(g.id));
    const updated = assignRoundRobin(ateliers, included, roundOffset);
    if (JSON.stringify(updated) === JSON.stringify(ateliers)) {
      return;
    }
    onChange(updated);
  }, [
    rotateInSeance,
    selectedGroupIds,
    roundOffset,
    ateliers,
    eleveGroups,
    onChange,
  ]);

  function handleToggle() {
    // If rotateInSeance was false => user wants to turn it ON
    if (!rotateInSeance) {
      // 1) Round-robin assignment
      if (!eleveGroups) return;
      if (ateliers.length === 0) return;

      const included = eleveGroups.filter(g => selectedGroupIds.includes(g.id));
      const updated = assignRoundRobin(ateliers, included, roundOffset);
      onChange(updated);
    } else {
      // rotateInSeance was true => user wants to turn it OFF
      const newAteliers = ateliers.map(a => {
        const firstRowPivot = (a.groupPivots || []).filter(
          p => p.position === 1,
        );
        return {
          ...a,
          groupPivots: firstRowPivot.map(fp => ({ ...fp, position: 1 })),
        };
      });
      onChange(newAteliers);
    }
  }

  // ---------- The "Rotate Groups" button
  // increments roundOffset => triggers useEffect => new assignment
  const handleRotateGroupsClick = () => {
    if (!eleveGroups) return;
    if (ateliers.length === 0) return;

    if (!rotateInSeance) {
      // SHIFT entire groupPivots from previous
      const newAteliers = ateliers.map((atelier, index) => {
        const prevIndex = (index - 1 + ateliers.length) % ateliers.length;
        return {
          ...atelier,
          groupPivots: ateliers[prevIndex].groupPivots || [],
        };
      });
      onChange(newAteliers);
    } else {
      // SHIFT offset => next => triggers reassign in useEffect
      const A = ateliers.length;
      setRoundOffset(prev => (prev + 1) % A);
    }
  };

  // ---------- Decide if "Rotate Groups" button is disabled
  const shouldDisplayRotateGroups =
    ateliers.length > 1 && (eleveGroups?.length || 0) > 1;

  // ---------- The actual rendering
  return (
    <div data-testid="ateliers-form" tw="mt-4 flex flex-col gap-y-4">
      <div>
        <h5 tw="text-neutral-quiet mb-1">{t('cahierJournal:ateliers.list')}</h5>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={ateliers.map(a => a.id)}
            strategy={verticalListSortingStrategy}
          >
            <div tw="space-y-2">
              {ateliers.map((a, index) => (
                <SortableAtelier
                  key={a.id}
                  atelier={a}
                  ref={el => {
                    atelierRefs.current[index] = el;
                  }}
                  disableGroups={rotateInSeance}
                  eleves={props.eleves}
                  groups={getGroupsForAtelier(
                    a.id,
                    ateliers,
                    regularGroups,
                    ephemeralGroups, // Use all ephemeral groups (saved + pending converted) for display
                  )}
                  onCreateGroup={({ name, eleves: newEleves, color }) =>
                    onCreateGroup({
                      name,
                      color,
                      elevesIds: newEleves.map(e => e.id),
                    })
                  }
                  onDeleteGroup={(groupId: string) => onDeleteGroup(groupId)}
                  onUpdateGroup={({ id, name, eleves: newEleves, color }) =>
                    onUpdateGroup({
                      id,
                      name,
                      color,
                      elevesIds: newEleves.map(e => e.id),
                    })
                  }
                  onChange={newValue => {
                    const updatedAteliers = ateliers.map(atelierValue =>
                      atelierValue.id === a.id ? newValue : atelierValue,
                    );
                    onChange(updatedAteliers);

                    const newGroupIds = getGroupIdsInAteliers(updatedAteliers);
                    if (
                      JSON.stringify(newGroupIds) !==
                      JSON.stringify(selectedGroupIds)
                    ) {
                      setSelectedGroupIds(newGroupIds);
                    }
                  }}
                  onDelete={() =>
                    onChange(ateliers.filter(aa => a.id !== aa.id))
                  }
                  defaultOpen={
                    a.id === createdAtelierId || a.id === openedAtelierId
                  }
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <div tw="flex items-center justify-start space-x-2 mt-2">
          <Button
            small
            iconSize="sm"
            onClick={() => {
              const newAtelier = newEmptyAtelier({
                title: t('cahierJournal:ateliers.modal.defaultAtelierTitle', {
                  count: ateliers.length + 1,
                }),
                cycle,
                supervision: 'Autonomie',
                position: ateliers.length,
              });
              setCreatedAtelierId(newAtelier.id);
              onChange([...ateliers, newAtelier]);
            }}
            icon={PlusIcon}
          >
            {t('cahierJournal:ateliers.modal.addAtelier')}
          </Button>
          {!rotateInSeance && (
            <Button
              onClick={handleRotateGroupsClick}
              small
              icon={ArrowPathIcon}
              disabled={!shouldDisplayRotateGroups}
            >
              {t('cahierJournal:actions.rotateGroups')}
            </Button>
          )}
        </div>
      </div>
      <div tw="flex flex-col gap-y-2">
        <h5 tw="text-neutral-quiet">
          {t('cahierJournal:ateliers.groupRotation')}
        </h5>
        <SwitchToggle
          label={t('cahierJournal:ateliers.enableGroupRotation')}
          onChange={handleToggle}
          checked={rotateInSeance}
        />
      </div>
      {rotateInSeance && (
        <>
          {eleveGroups && eleveGroups.length > 0 && (
            <GroupSelectorInline
              allGroups={eleveGroups}
              groups={[...regularGroups, ...savedEphemeralGroups]}
              pendingEphemeralGroups={allPendingEphemeralGroups}
              eleves={props.eleves}
              selectedGroupIds={selectedGroupIds}
              onChangeSelected={setSelectedGroupIds}
              onCreateGroup={({ name, eleves: newEleves, color }) =>
                onCreateGroup({
                  name,
                  color,
                  elevesIds: newEleves.map(e => e.id),
                })
              }
              onDeleteGroup={(groupId: string) => onDeleteGroup(groupId)}
              onUpdateGroup={({ id, name, eleves: newEleves, color }) =>
                onUpdateGroup({
                  id,
                  name,
                  color,
                  elevesIds: newEleves.map(e => e.id),
                })
              }
              onPendingEphemeralGroupsChange={
                handlePendingEphemeralGroupsChange
              }
            />
          )}

          <div>
            <p tw="text-sm font-semibold mb-2">
              {t('cahierJournal:ateliers.rotationTable')}
            </p>
            <RotateInSeanceTable
              ateliers={ateliers}
              groups={eleveGroups || []}
              startTime={start}
              endTime={end}
            />
          </div>
          <div tw="flex items-center justify-start space-x-2">
            <Button
              onClick={handleRotateGroupsClick}
              small
              icon={ArrowPathIcon}
              disabled={!shouldDisplayRotateGroups}
            >
              {t('cahierJournal:actions.rotateGroups')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
